"use client";
import { useState, useEffect, useRef, ChangeEvent } from "react";
import { Modal, Form, Col, Row, Button } from "react-bootstrap";
import moment from "moment";
import common from "../../utils/common";

import Datagrid from "../../components/Datagrid/Datagrid";
import { MdOutlineCalendarMonth } from "react-icons/md";
import ModalIconTitle from "../../components//ModalIconTitle";

import type toast from "react-hot-toast";
import { cal_place, cal_record, cal_calendar } from "../../class/prisma/db";
import { AgGridReact } from "ag-grid-react";
import TagSelector, { Tag, getSelectedOptionsById } from "../ReactSelect";

import { SystemMsg } from "../../utils/SystemMsg";

import TextInput from "./Input/TextInput";
import { CalendarViewObject } from "../../class/calendar.schema";
import { MultiValue, SingleValue } from "react-select";

import { AxiosResponse } from "axios";
import { BackendApi } from "../../utils/BackendApi";
import { ApiRoute } from "../../utils/ApiRoute";
import { UpdateRecordMultiRequest } from "../../class/record.schema";
import { useI18n } from "../../i18n";

type EventMultiModalProps = {
  show: boolean;
  toast: typeof toast;
  data: cal_record | cal_record[] | undefined;
  calendars: CalendarViewObject[];
  places: cal_place[];
  mode: string;
  onClose: () => void;
  onConfirmClick: (
    resp: AxiosResponse,
    mode?: string,
    data?: UpdateRecordMultiRequest,
  ) => void;
};

function EventMultiModal({
  toast,
  show,
  data,
  calendars,
  places,
  mode,
  onClose,
  onConfirmClick,
}: EventMultiModalProps) {
  const dialogClassName = "modal-47w";
  const [modeState, setModeState] = useState<"delete" | "edit">(`edit`);

  const [title, setTitle] = useState("Edit Records");
  const [rowData, setRowData] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const { t } = useI18n();
  const dateRenderer = (param: any) => {
    if (!param || !param.value) {
      return "";
    }
    if (!param.value) {
      return "";
    }

    const dateObj = moment(param.value);
    if (!dateObj.isValid()) {
      return "Invalid Date";
    }
    return dateObj.format(`YYYY-MM-DD HH:mm`);
  };

  const colDefs = [
    { field: "id", width: 80 },
    { field: "name", flex: 2 },
    {
      field: "start_date",
      headerName: "Start",
      width: 150,
      cellRenderer: (params: any) => {
        return dateRenderer(params);
      },
    },
    {
      field: "end_date",
      headerName: "End",
      width: 150,
      cellRenderer: (params: any) => {
        return dateRenderer(params);
      },
    },
  ];

  const [prefix, setPrefix] = useState(``);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [paginationPage, setPaginationPage] = useState(0);

  const [calendarId, setCalendarId] = useState(-1);
  const [calendarOptions, setCalendarOptions] = useState<Array<Tag>>([]);

  const [placeId, setPlaceId] = useState(-1);
  const [placeOptions, setPlaceOptions] = useState<Array<Tag>>([]);

  useEffect(() => {
    if (!show) {
      return;
    }

    if (!Array.isArray(data) || data.length === 0) {
      toast.error(SystemMsg.Common.unableToLoadData);
      return;
    }
    setRowData(data);
    const modeLower = mode.toString().toLowerCase();
    if (modeLower === `delete`) {
      setModeState(`delete`);
    }
    if (modeLower === `edit`) {
      setModeState(`edit`);
    }
  }, [show, mode, data]);

  useEffect(() => {
    if (Array.isArray(calendars)) {
      const options = [];
      for (const item of calendars as unknown as CalendarViewObject[]) {
        options.push({
          value: item.id.toString(),
          label: item.name,
          color: item.color ?? "#fff",
        });
      }
      setCalendarOptions(options);
    }
  }, [calendars]);

  useEffect(() => {
    if (Array.isArray(places)) {
      const options = [];
      for (const item of places as unknown as cal_place[]) {
        options.push({
          value: item.id.toString(),
          label: item.name,
        });
      }
      setPlaceOptions(options);
    }
  }, [places]);

  useEffect(() => {
    setPage(1);
    setPaginationPage(0);
    const isEdit = modeState === `edit`;
    if (Array.isArray(rowData) && rowData.length > 0) {
      setPageCount(common.getPageCount(rowData.length, pageSize));
      setTitle(
        isEdit
          ? `Edit Records (${rowData.length})`
          : `Delete Records (${rowData.length})`,
      );
    }
  }, [rowData, pageSize]);

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    }

    gridRef.current?.api?.deselectAll();
    setRowData([]);
    setPaginationPage(0);
    setPage(1);
    setCalendarId(-1);
    setPlaceId(-1);
    setPrefix(``);
  };

  const handlePageClick = (event: { selected: number }) => {
    if (!event || event?.selected == null || Number.isNaN(event.selected)) {
      return;
    }
    setPage(event.selected + 1);
    setPaginationPage(event.selected);
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (
      !event ||
      !event?.target ||
      event?.target?.value == null ||
      Number.isNaN(event?.target?.value)
    ) {
      return;
    }
    setPageSize(Number(event.target.value));
  };

  const topLeftElement = (
    <div style={{ maxWidth: "60%" }}>
      <TextInput
        value={prefix}
        label={t("fields.prefix")}
        placeholder={t("fields.prefix")}
        setValueFunc={(value: string | number) => {
          setPrefix(value as string);
        }}
      />
      <Row className="mb-3">
        <Form.Group as={Col} controlId="eventModal.calendarId" className="mt-2">
          <TagSelector
            isMulti={false}
            options={calendarOptions as Tag[]}
            defaultValue={getSelectedOptionsById(calendarOptions, calendarId)}
            onChange={(event: MultiValue<any> | SingleValue<any>) => {
              if (!event || !Number.isInteger(Number(event.value))) {
                setCalendarId(-1);
                return;
              }
              setCalendarId(Number(event.value));
            }}
            placeholder="Calendar"
          />
        </Form.Group>
        <Form.Group as={Col} controlId="eventModal.placeId" className="mt-2">
          <TagSelector
            isMulti={false}
            options={placeOptions as Tag[]}
            defaultValue={getSelectedOptionsById(placeOptions, placeId)}
            onChange={(event: MultiValue<any> | SingleValue<any>) => {
              if (!event || !Number.isInteger(Number(event.value))) {
                setPlaceId(-1);
                return;
              }
              setPlaceId(Number(event.value));
            }}
            placeholder="Place"
          />
        </Form.Group>
      </Row>
    </div>
  );

  const confirmClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const isEdit = modeState === `edit`;
    const ids = rowData?.map((x) => Number(x.id));

    const sendData: UpdateRecordMultiRequest = isEdit
      ? {
          ids: ids,
          calendar_id: calendarId !== -1 ? calendarId : undefined,
          place_id: placeId !== -1 ? placeId : undefined,
        }
      : {
          ids: ids,
          is_deleted: true,
        };

    const succFunc = (res: AxiosResponse) => {
      const success = res.status === 200;
      if (success) {
        toast.success(
          isEdit
            ? SystemMsg.Record.updateMultiSuccess(ids)
            : SystemMsg.Record.deleteMultiSuccess(ids),
        );
      }

      if (typeof onConfirmClick === "function") {
        onConfirmClick(res, modeState, sendData);
      }
    };
    const errFunc = (res: AxiosResponse | string) => {
      toast.error(
        isEdit
          ? SystemMsg.Record.updateMultiFailed(ids)
          : SystemMsg.Record.deleteMultiFailed(ids),
      );
      if (typeof onConfirmClick === "function") {
        onConfirmClick(res as AxiosResponse, modeState, sendData);
      }
    };

    BackendApi.put(`${ApiRoute.records}/batch`, sendData, succFunc, errFunc);

    handleClose();
  };

  return (
    <>
      <Modal
        size="lg"
        show={show}
        onHide={handleClose}
        backdrop="static"
        centered
        dialogClassName={dialogClassName}
      >
        <Modal.Header
          style={{ border: "none", paddingBottom: "4px" }}
          closeButton
        >
          <Modal.Title>
            <ModalIconTitle
              title={title}
              iconElement={<MdOutlineCalendarMonth />}
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Datagrid
            height="50vh"
            rowSelect="none"
            gridRef={gridRef}
            rowData={rowData}
            colDefs={colDefs}
            hasPagination
            handlePageClick={handlePageClick}
            handlePageSizeChange={handlePageSizeChange}
            paginatePage={paginationPage}
            paginatePageCount={pageCount}
            topLeftElement={modeState === `edit` && topLeftElement}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={confirmClick}>
            {t("common.confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default EventMultiModal;
